import { TaskAPI } from '@business/api/task';
import { TaskId, TaskOrderRequest, TaskResponse } from '@business/entities/task';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

export class TaskBloc extends BaseBloc {
  private readonly taskRepo = new Repository<TaskResponse>({
    getItemId: task => task.id,
  });

  readonly tasks$ = this.taskRepo.items$;
  readonly selectTask = this.taskRepo.selectItem;

  onReset() {
    this.taskRepo.reset();
  }

  fetchTasks = () => {
    return TaskAPI.fetchTasks().pipe(this.taskRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_help_articles_error') }));
  };

  fetchTask = (id: TaskId) => {
    return TaskAPI.fetchTask(id).pipe(
      this.taskRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_help_articles_error') }),
    );
  };

  createTask = (task: Partial<TaskResponse>, parentId?: TaskId) => {
    return TaskAPI.createTask(task, parentId).pipe(
      this.taskRepo.ops.addOne(item => item),
      tap(() => (parentId ? this.fetchTask(parentId).subscribe() : of(null))),
      handleMessage({
        type: requestMessage('create_task'),
      }),
    );
  };

  orderTasks = (orders: TaskOrderRequest) => {
    const updates = orders.map(item => ({ id: item.id, changes: () => ({ menuOrder: item.menuOrder }) }));
    this.taskRepo.updateMany(updates);
    return TaskAPI.setOrders(orders);
  };

  updateTask = (id: TaskId, task: Partial<TaskResponse>, taskId?: TaskId) => {
    this.taskRepo.updateOne({ id, changes: () => task });
    return of(this.taskRepo.getItem(id)).pipe(
      switchMap(oldTask => {
        if (oldTask) {
          return TaskAPI.updateTask(id, { ...oldTask, ...task });
        } else if (taskId) {
          const parentTask = this.taskRepo.getItem(taskId);
          if (parentTask) {
            const newTask = parentTask.children.find(item => item.id === id);
            return TaskAPI.updateTask(id, { ...newTask, ...task });
          }
        }
        return of(null);
      }),
      tap(item => {
        if (item) {
          if (taskId) {
            this.fetchTask(taskId).subscribe();
          } else {
            this.taskRepo.upsertOne({ item });
          }
        }
      }),
    );
  };

  setComplete = (id: TaskId, isCompleted: boolean) => {
    // this.taskRepo.updateOne({ id, changes: () => ({ isCompleted }) });
    return TaskAPI.setComplete(id, isCompleted).pipe(
      tap(item => {
        setTimeout(() => {
          this.taskRepo.upsertOne({ item });
        }, 2000);
      }),
    );
  };

  deleteTask = (id: TaskId, taskId?: TaskId) => {
    return TaskAPI.deleteTask(id).pipe(tap(() => (taskId ? this.fetchTask(taskId).subscribe() : this.taskRepo.ops.removeOne(() => id))));
  };
}
